import LoadingBox from 'components/Common/Loading/LoadingBox'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { fillArray, nonNullable } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { Fragment, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import { viewItemListExperiences } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import OfferListInfiniteScroll from 'components/OfferList/OfferListInfiniteScroll'
import Group from 'components/utils/Group'

const SkeletonTile = styled(LoadingBox)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  min-height: ${rem(380)};
`

const ListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
  gap: ${rem(36)} ${rem(16)};
  margin-top: ${rem(36)};
  min-height: ${rem(200)};
  position: relative;

  @media (min-width: 2048px) {
    grid-template-columns: repeat(6, minmax(${rem(300)}, 1fr));
  }
`

interface Props {
  placeId: string
  sortBy?: Experiences.SortTypes
  categoryCodes?: Array<number>
  priceLte?: number
  priceGte?: number
  excludeIds?: Array<string>
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  collections?: Array<string>
}

function ExperienceDenseSearchList(props: Props) {
  const {
    placeId,
    sortBy,
    categoryCodes,
    priceGte,
    priceLte,
    excludeIds,
    luxPlusFeatures,
    collections,
  } = props
  const [page, setPage] = useState<number>(1)
  const limit = page * 4
  const [list, experiences] = useExperienceSearch({ placeId, limit, sortBy, excludeIds }, { categoryCodes, priceGte, priceLte, luxPlusFeatures, collections })
  const hasMore = !!list && experiences.length < list.ids.length
  const onLoadMore = useCallback(() => setPage(p => p + 1), [])

  useEffect(() => {
    if (experiences?.length) {
      const event = viewItemListExperiences(nonNullable(experiences))
      if (event) {
        addGTMEvent(event)
      }
    }
  }, [experiences])

  return (
    <div>
      <ListGrid>
        {(!list || list.fetching) && fillArray(4).map((id) => <SkeletonTile key={id} />)}
        {list && !list.fetching && experiences.map((exp, idx) => <Fragment key={`${exp?.id}-${idx}`}>
          {!exp && <SkeletonTile />}
          {exp && <ExperienceCompactSearchTile
            experience={exp}
            position={idx}
          />}
        </Fragment>)}
      </ListGrid>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="center"
      >
        <OfferListInfiniteScroll
          onLoadMore={onLoadMore}
          isLoading={list?.fetching}
          hasMore={hasMore}
        />
      </Group>
    </div>
  )
}

export default ExperienceDenseSearchList
